import { SIGNUP_HEARD_ABOUT_SOURCES, SIGNUP_REGIONS, SIGNUP_ROLES } from '@server/signup-answers';
import { z } from 'zod';

import type { MfaMethod } from './rudderstack-auth-client';

// Lowercased like the web app does: config-backend matches some emails case-sensitively (/forgotPassMail).
const email = z
  .string()
  .trim()
  .toLowerCase()
  .min(1, 'Enter your email')
  .pipe(z.email({ error: 'Enter a valid email' }));

export const signInSchema = z.object({
  email,
  password: z.string().min(1, 'Enter your password'),
});
export type SignInValues = z.infer<typeof signInSchema>;

export const emailSchema = z.object({ email });
export type EmailValues = z.infer<typeof emailSchema>;

/** Backup codes are shown grouped with dashes; config-backend wants the bare 12 characters. */
export function normalizeMfaCode(method: MfaMethod, code: string): string {
  return method === 'BACKUP_CODE' ? code.replace(/[\s-]/g, '').toUpperCase() : code.replace(/\s/g, '');
}

export const mfaCodeSchema = z
  .object({
    method: z.enum(['TOTP', 'SMS', 'BACKUP_CODE']),
    code: z.string(),
  })
  .superRefine((values, ctx) => {
    const code = normalizeMfaCode(values.method, values.code);
    if (values.method === 'BACKUP_CODE') {
      if (!/^[0-9A-Z]{12}$/.test(code)) {
        ctx.addIssue({ code: 'custom', path: ['code'], message: 'Enter a 12-character backup code' });
      }
    } else if (!/^\d{6}$/.test(code)) {
      ctx.addIssue({ code: 'custom', path: ['code'], message: 'Enter the 6-digit code' });
    }
  });
export type MfaCodeValues = z.infer<typeof mfaCodeSchema>;

/** Mirrors config-backend's `USER_NAME_REGEX`. */
const NAME_PATTERN = /^[A-Za-z0-9 ._-]{2,50}$/;
/** Mirrors config-backend's `ORGANIZATION_NAME_REGEX`. */
const ORGANIZATION_PATTERN = /^[A-Za-z0-9 ._-]{2,50}$/;
const NAME_RULE = 'Use 2 to 50 letters, numbers, spaces, dots, hyphens or underscores';

const PASSWORD_FIELDS: readonly PropertyKey[] = ['password', 'confirmPassword'];

/** An invitee joins a Lookout workspace, not a RudderStack organization, so it names none. */
export function signupCompleteSchema(invitee: boolean) {
  const organization = z.string().trim();
  return z
    .object({
      name: z.string().trim().min(1, 'Enter your name').regex(NAME_PATTERN, NAME_RULE),
      organization: invitee
        ? organization
        : organization.min(1, 'Enter your organization name').regex(ORGANIZATION_PATTERN, NAME_RULE),
      role: z.enum(SIGNUP_ROLES, { message: 'Select your role' }),
      password: z.string().min(1, 'Enter a password'),
      confirmPassword: z.string().min(1, 'Enter the password again'),
      // An invitee gets no RudderStack workspace, so the page does not ask it for a region.
      region: z.enum(SIGNUP_REGIONS),
      heardAboutSource: z.enum(SIGNUP_HEARD_ABOUT_SOURCES).optional(),
    })
    .superRefine(
      (values, ctx) => {
        if (values.password !== values.confirmPassword) {
          ctx.addIssue({ code: 'custom', path: ['confirmPassword'], message: 'The passwords do not match' });
        }
      },
      // Also while other fields are invalid, so that the form shows every error at once.
      { when: ({ issues }) => issues.every((issue) => !PASSWORD_FIELDS.includes(issue.path?.[0] ?? '')) },
    );
}
export type SignupCompleteValues = z.infer<ReturnType<typeof signupCompleteSchema>>;

export const resetPasswordSchema = z
  .object({
    password: z.string().min(1, 'Enter a new password'),
    confirmPassword: z.string().min(1, 'Enter the password again'),
  })
  .superRefine((values, ctx) => {
    if (values.confirmPassword && values.password !== values.confirmPassword) {
      ctx.addIssue({ code: 'custom', path: ['confirmPassword'], message: 'The passwords do not match' });
    }
  });
export type ResetPasswordValues = z.infer<typeof resetPasswordSchema>;

/** config-backend is the authority; this is only the hint. */
export const PASSWORD_RULE_HINT =
  'At least 12 characters with upper and lower case letters, a number and a symbol, or at least 20 characters.';
